import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  Users, 
  MessageSquare, 
  Shield, 
  Ban, 
  CheckCircle,
  Trash2,
  Eye,
  EyeOff,
  BarChart3,
  LogOut,
  Key,
  X,
  Gift,
  Copy,
  Link,
  Clock,
  UserPlus,
  Mail,
  Bell,
  Briefcase,
  Building,
  MapPin,
  Phone,
  Globe,
  Plus,
  Megaphone,
  Pencil,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Specializations and regions constants
const SPECIALIZATIONS = {
  mechatronics: ['mechanical_electrical_assembly', 'maintenance'],
  mechanical: ['mechanical_assembly', 'maintenance', 'mechanical_designer'],
  electrical: ['switchboards', 'machine_line_edge', 'robotics', 'assembly', 'maintenance', 'electrical_designer'],
  software: ['software_development', 'plc_programming', 'robotics_programming', 'support', 'ai_integration']
};

const REGIONS = ['europe', 'asia', 'america', 'south_america', 'africa'];

const CERTIFICATIONS = {
  plc: ['siemens', 'rockwell', 'mitsubishi', 'omron', 'beckhoff', 'schneider'],
  robotics: ['fanuc', 'abb', 'kuka', 'universal_robots', 'yaskawa', 'kawasaki'],
  electrical_sep: ['g1_operation', 'g2_supervision', 'g3_design'],
  safety_bhp: ['basic_bhp', 'periodic_bhp', 'supervisor_bhp', 'first_aid']
};

const AdminLoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('admin_token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      onLogin();
      toast.success('Login successful');
    } catch (error) {
      toast.error('Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle>Admin Panel</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-red-600 hover:bg-red-700"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = ({ onLogout }) => {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [reviewsPage, setReviewsPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Trial invites state
  const [trialInvites, setTrialInvites] = useState([]);
  const [trialInvitesPage, setTrialInvitesPage] = useState(1);
  const [trialInvitesTotal, setTrialInvitesTotal] = useState(0);
  const [showCreateTrialModal, setShowCreateTrialModal] = useState(false);
  const [newTrialData, setNewTrialData] = useState({ email: '', note: '', duration_days: 30, invite_type: 'any' });
  const [creatingTrial, setCreatingTrial] = useState(false);
  const [createdInviteUrl, setCreatedInviteUrl] = useState(null);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [expiringUsers, setExpiringUsers] = useState([]);
  
  // Professionals management state
  const [professionals, setProfessionals] = useState([]);
  const [professionalsPage, setProfessionalsPage] = useState(1);
  const [professionalsTotal, setProfessionalsTotal] = useState(0);
  const [showCreateProfessionalModal, setShowCreateProfessionalModal] = useState(false);
  const [creatingProfessional, setCreatingProfessional] = useState(false);
  const [newProfessionalData, setNewProfessionalData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    address: '',
    city: '',
    country: 'Italia',
    phone: '',
    website: '',
    description: '',
    specializations: {},
    regions: [],
    entity_type: 'company',
    certifications: {}
  });
  const [acceptedTermsPro, setAcceptedTermsPro] = useState(false);

  // Banner ads management state
  const [banners, setBanners] = useState([]);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [savingBanner, setSavingBanner] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    company: '',
    description: '',
    image_url: '',
    link_url: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    target_language: 'all',
    active: true,
  });

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchReviews();
    fetchTrialInvites();
    fetchExpiringUsers();
    fetchProfessionals();
    fetchBanners();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async (page = 1, searchTerm = null) => {
    try {
      const s = searchTerm !== null ? searchTerm : userSearch;
      const searchParam = s && s.trim() ? `&search=${encodeURIComponent(s.trim())}` : '';
      const response = await axios.get(`${API}/admin/users?page=${page}&limit=10${searchParam}`);
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
      setUsersPage(page);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/admin/reviews?page=${page}&limit=10`);
      setReviews(response.data.reviews);
      setReviewsTotal(response.data.total);
      setReviewsPage(page);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchTrialInvites = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/admin/trial-invites?page=${page}&limit=10`);
      setTrialInvites(response.data.invites);
      setTrialInvitesTotal(response.data.total);
      setTrialInvitesPage(page);
    } catch (error) {
      console.error('Error fetching trial invites:', error);
    }
  };

  const handleCreateTrialInvite = async (e) => {
    e.preventDefault();
    setCreatingTrial(true);
    
    try {
      const response = await axios.post(`${API}/admin/trial-invites`, {
        ...newTrialData,
        invite_type: newTrialData.invite_type === 'any' ? null : newTrialData.invite_type,
        origin_url: window.location.origin
      });
      toast.success('Invito creato con successo!');
      setCreatedInviteUrl(response.data.invite_url);
      fetchTrialInvites(trialInvitesPage);
      fetchStats();
      setNewTrialData({ email: '', note: '', duration_days: 30, invite_type: 'any' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingTrial(false);
    }
  };

  const handleDeleteTrialInvite = async (inviteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo invito?')) return;
    
    try {
      await axios.delete(`${API}/admin/trial-invites/${inviteId}`);
      toast.success('Invito eliminato');
      fetchTrialInvites(trialInvitesPage);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success('Link copiato!');
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Link copiato!');
      }
    } catch (err) {
      // Show the link in a prompt so user can copy manually
      window.prompt('Copia questo link:', text);
    }
  };

  const fetchExpiringUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/trial-users-expiring?days=14`);
      setExpiringUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching expiring users:', error);
    }
  };

  const handleSendExpiryNotifications = async () => {
    setSendingNotifications(true);
    try {
      const response = await axios.post(`${API}/admin/send-trial-expiry-notifications`);
      const { sent, failed, total_found } = response.data;
      
      if (sent > 0) {
        toast.success(`Email inviate: ${sent}/${total_found}`);
      } else if (total_found === 0) {
        toast.info('Nessun utente con trial in scadenza tra 7 giorni');
      } else {
        toast.warning(`Invio fallito: ${failed}/${total_found}`);
      }
      
      fetchExpiringUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio notifiche');
    } finally {
      setSendingNotifications(false);
    }
  };

  const fetchProfessionals = async (page = 1) => {
    try {
      const response = await axios.get(`${API}/admin/professionals?page=${page}&limit=10`);
      setProfessionals(response.data.professionals || []);
      setProfessionalsTotal(response.data.total || 0);
      setProfessionalsPage(page);
    } catch (error) {
      console.error('Error fetching professionals:', error);
    }
  };

  const handleCreateProfessional = async (e) => {
    e.preventDefault();
    
    if (!newProfessionalData.email || !newProfessionalData.password || !newProfessionalData.name) {
      toast.error('Email, password e nome sono obbligatori');
      return;
    }
    
    if (!acceptedTermsPro) {
      toast.error('Devi confermare l\'accettazione dei Termini di Servizio');
      return;
    }
    
    setCreatingProfessional(true);
    try {
      await axios.post(`${API}/admin/professionals`, newProfessionalData);
      toast.success('Professionista creato con successo!');
      setShowCreateProfessionalModal(false);
      setAcceptedTermsPro(false);
      setNewProfessionalData({
        email: '',
        password: '',
        name: '',
        company_name: '',
        address: '',
        city: '',
        country: 'Italia',
        phone: '',
        website: '',
        description: '',
        specializations: {},
        regions: [],
        entity_type: 'company',
        certifications: {}
      });
      fetchProfessionals(professionalsPage);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingProfessional(false);
    }
  };

  const handleDeleteProfessional = async (professionalId, email) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il professionista ${email}? Questa azione è irreversibile.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/professionals/${professionalId}`);
      toast.success('Professionista eliminato');
      fetchProfessionals(professionalsPage);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  // =============== BANNER ADS MANAGEMENT ===============
  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API}/admin/banners`);
      setBanners(res.data || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
    }
  };

  const resetBannerForm = () => {
    setBannerForm({
      title: '',
      company: '',
      description: '',
      image_url: '',
      link_url: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      target_language: 'all',
      active: true,
    });
    setEditingBanner(null);
  };

  const openCreateBanner = () => {
    resetBannerForm();
    setShowBannerModal(true);
  };

  const openEditBanner = (banner) => {
    setEditingBanner(banner);
    setBannerForm({
      title: banner.title || '',
      company: banner.company || '',
      description: banner.description || '',
      image_url: banner.image_url || '',
      link_url: banner.link_url || '',
      start_date: (banner.start_date || '').split('T')[0] || new Date().toISOString().split('T')[0],
      end_date: (banner.end_date || '').split('T')[0] || new Date().toISOString().split('T')[0],
      target_language: banner.target_language || 'all',
      active: banner.active !== false,
    });
    setShowBannerModal(true);
  };

  const handleBannerImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Immagine troppo grande (max 2MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setBannerForm((prev) => ({ ...prev, image_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const saveBanner = async () => {
    if (!bannerForm.title || !bannerForm.company || !bannerForm.description || !bannerForm.link_url) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    setSavingBanner(true);
    try {
      const payload = {
        title: bannerForm.title,
        company: bannerForm.company,
        description: bannerForm.description,
        image_url: bannerForm.image_url,
        link_url: bannerForm.link_url,
        start_date: new Date(bannerForm.start_date).toISOString(),
        end_date: new Date(bannerForm.end_date + 'T23:59:59').toISOString(),
        target_language: bannerForm.target_language === 'all' ? null : bannerForm.target_language,
        active: bannerForm.active,
      };
      if (editingBanner) {
        await axios.put(`${API}/admin/banners/${editingBanner.id}`, payload);
        toast.success('Banner aggiornato');
      } else {
        await axios.post(`${API}/admin/banners`, payload);
        toast.success('Banner creato');
      }
      setShowBannerModal(false);
      resetBannerForm();
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingBanner(false);
    }
  };

  const deleteBanner = async (id, title) => {
    if (!window.confirm(`Eliminare il banner "${title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/banners/${id}`);
      toast.success('Banner eliminato');
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const toggleBannerActive = async (banner) => {
    try {
      await axios.put(`${API}/admin/banners/${banner.id}`, { active: !banner.active });
      toast.success(banner.active ? 'Banner disattivato' : 'Banner attivato');
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const toggleSpecialization = (category, spec) => {
    setNewProfessionalData(prev => {
      const current = prev.specializations[category] || [];
      const updated = current.includes(spec)
        ? current.filter(s => s !== spec)
        : [...current, spec];
      
      const newSpecs = { ...prev.specializations };
      if (updated.length === 0) {
        delete newSpecs[category];
      } else {
        newSpecs[category] = updated;
      }
      
      return { ...prev, specializations: newSpecs };
    });
  };

  const toggleRegion = (region) => {
    setNewProfessionalData(prev => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter(r => r !== region)
        : [...prev.regions, region]
    }));
  };

  const toggleCertification = (category, cert) => {
    setNewProfessionalData(prev => {
      const current = prev.certifications[category] || [];
      const updated = current.includes(cert)
        ? current.filter(c => c !== cert)
        : [...current, cert];
      
      const newCerts = { ...prev.certifications };
      if (updated.length === 0) {
        delete newCerts[category];
      } else {
        newCerts[category] = updated;
      }
      
      return { ...prev, certifications: newCerts };
    });
  };

  const handleToggleBlock = async (userId, currentlyBlocked) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_blocked: !currentlyBlocked });
      toast.success(currentlyBlocked ? 'User unblocked' : 'User blocked');
      fetchUsers(usersPage);
      fetchStats();
    } catch (error) {
      toast.error('Error updating user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Sei sicuro di voler cancellare l'iscrizione di "${userName}"? Questa azione è irreversibile.`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Iscrizione cancellata');
      fetchUsers(usersPage);
      fetchStats();
    } catch (error) {
      toast.error('Errore durante la cancellazione');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    const newPassword = window.prompt(`Inserisci la nuova password per "${userName}":`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error('La password deve avere almeno 6 caratteri');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/users/${userId}/reset-password`, { new_password: newPassword });
      toast.success('Password resettata con successo');
    } catch (error) {
      toast.error('Errore durante il reset della password');
    }
  };

  const [convertDialog, setConvertDialog] = useState(null);
  const [convertForm, setConvertForm] = useState({
    company_name: '', entity_type: 'company', phone: '', address: '', city: '', country: 'Italia',
    description: '', specializations: {}, regions: [], certifications: {}
  });

  const openConvertDialog = (user) => {
    setConvertForm({
      name: user.name || '',
      company_name: '', entity_type: 'company', phone: '', address: '', city: '', country: 'Italia',
      description: '', specializations: {}, regions: [], certifications: {}
    });
    setConvertDialog(user);
  };

  const handleConvertToProfessional = async () => {
    if (!convertDialog) return;
    if (!convertForm.company_name.trim()) {
      toast.error('Inserisci il nome azienda/attività');
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${convertDialog.id}/convert-to-professional`, convertForm);
      toast.success(`${convertDialog.name} convertito in professionista!`);
      setConvertDialog(null);
      fetchUsers(usersPage);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la conversione');
    }
  };

  const handleToggleHideReview = async (reviewId, currentlyHidden) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}`, { is_hidden: !currentlyHidden });
      toast.success(currentlyHidden ? 'Review shown' : 'Review hidden');
      fetchReviews(reviewsPage);
      fetchStats();
    } catch (error) {
      toast.error('Error updating review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`);
      toast.success('Review deleted');
      fetchReviews(reviewsPage);
      fetchStats();
    } catch (error) {
      toast.error('Error deleting review');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    delete axios.defaults.headers.common['Authorization'];
    onLogout();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      toast.success('Password cambiata con successo!');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel cambio password';
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Cambia Password
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPasswordModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Password Attuale</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    required
                    data-testid="current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nuova Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    required
                    minLength={6}
                    data-testid="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Conferma Nuova Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    required
                    data-testid="confirm-password"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                    disabled={changingPassword}
                    data-testid="submit-password-change"
                  >
                    {changingPassword ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Salva
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-red-500" />
            <h1 className="text-xl font-semibold">AUTOMAT.IO Admin Panel</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              className="text-white hover:bg-slate-800"
              onClick={() => setShowPasswordModal(true)}
              data-testid="change-password-btn"
            >
              <Key className="h-4 w-4 mr-2" />
              Cambia Password
            </Button>
            <Button 
              variant="ghost" 
              className="text-white hover:bg-slate-800"
              onClick={handleLogout}
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_users}</p>
                <p className="text-xs text-slate-500">Total Users</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Users className="h-6 w-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_professionals}</p>
                <p className="text-xs text-slate-500">Professionals</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Ban className="h-6 w-6 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.blocked_users}</p>
                <p className="text-xs text-slate-500">Blocked</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <MessageSquare className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_reviews}</p>
                <p className="text-xs text-slate-500">Reviews</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <EyeOff className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.hidden_reviews}</p>
                <p className="text-xs text-slate-500">Hidden Reviews</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <BarChart3 className="h-6 w-6 text-teal-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.total_payments}</p>
                <p className="text-xs text-slate-500">Payments</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Gift className="h-6 w-6 text-pink-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.trial_users || 0}</p>
                <p className="text-xs text-slate-500">Trial Users</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Link className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-2xl font-bold">{stats.active_trial_invites || 0}</p>
                <p className="text-xs text-slate-500">Active Invites</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="users">
          <TabsList className="mb-6">
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="h-4 w-4 mr-2" />
              Users ({usersTotal})
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">
              <MessageSquare className="h-4 w-4 mr-2" />
              Reviews ({reviewsTotal})
            </TabsTrigger>
            <TabsTrigger value="trials" data-testid="tab-trials">
              <Gift className="h-4 w-4 mr-2" />
              Trial Invites ({trialInvitesTotal})
            </TabsTrigger>
            <TabsTrigger value="professionals" data-testid="tab-professionals">
              <Briefcase className="h-4 w-4 mr-2" />
              Professionisti ({professionalsTotal})
            </TabsTrigger>
            <TabsTrigger value="banners" data-testid="tab-banners">
              <Megaphone className="h-4 w-4 mr-2" />
              Banner Ads ({banners.length})
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Registered Users</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Search bar */}
                <div className="flex gap-2 mb-4">
                  <Input
                    placeholder="Cerca per email, nome o azienda..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') fetchUsers(1, userSearch); }}
                    className="max-w-md"
                    data-testid="admin-user-search"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fetchUsers(1, userSearch)}
                    data-testid="admin-user-search-btn"
                  >
                    Cerca
                  </Button>
                  {userSearch && (
                    <Button
                      variant="ghost"
                      onClick={() => { setUserSearch(''); fetchUsers(1, ''); }}
                      data-testid="admin-user-search-clear"
                    >
                      Pulisci
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Type</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Created</th>
                        <th className="text-left p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium">{user.name}</td>
                          <td className="p-3 text-slate-600">{user.email}</td>
                          <td className="p-3">
                            <Badge variant="outline" className={
                              user.user_type === 'professional' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-green-100 text-green-700'
                            }>
                              {user.user_type}
                            </Badge>
                          </td>
                          <td className="p-3">
                            {user.is_blocked ? (
                              <Badge variant="destructive">Blocked</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-100 text-green-700">Active</Badge>
                            )}
                          </td>
                          <td className="p-3 text-slate-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3 flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResetPassword(user.id, user.name)}
                              data-testid={`reset-pw-${user.id}`}
                            >
                              <Key className="h-3 w-3 mr-1" />
                              Reset PW
                            </Button>
                            {!user.has_professional_profile && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                onClick={() => openConvertDialog(user)}
                                data-testid={`convert-pro-${user.id}`}
                              >
                                <UserPlus className="h-3 w-3 mr-1" />
                                Professionista
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(user.id, user.name)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Cancella
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={usersPage === 1}
                    onClick={() => fetchUsers(usersPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-2 text-sm">
                    Page {usersPage} of {Math.ceil(usersTotal / 10)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={usersPage >= Math.ceil(usersTotal / 10)}
                    onClick={() => fetchUsers(usersPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle>All Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No reviews yet</p>
                  ) : (
                    reviews.map((review) => (
                      <div 
                        key={review.id} 
                        className={`border rounded-md p-4 ${review.is_hidden ? 'bg-red-50 border-red-200' : ''}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{review.reviewer_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {review.reviewer_type}
                              </Badge>
                              {review.is_hidden && (
                                <Badge variant="destructive" className="text-xs">Hidden</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 mb-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span key={star} className={star <= review.rating ? 'text-yellow-400' : 'text-slate-300'}>
                                  ★
                                </span>
                              ))}
                            </div>
                            <p className="text-slate-700">{review.comment}</p>
                            <p className="text-xs text-slate-400 mt-2">
                              {new Date(review.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleHideReview(review.id, review.is_hidden)}
                              data-testid={`toggle-hide-${review.id}`}
                            >
                              {review.is_hidden ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteReview(review.id)}
                              data-testid={`delete-review-${review.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Pagination */}
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={reviewsPage === 1}
                    onClick={() => fetchReviews(reviewsPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-2 text-sm">
                    Page {reviewsPage} of {Math.ceil(reviewsTotal / 10)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={reviewsPage >= Math.ceil(reviewsTotal / 10)}
                    onClick={() => fetchReviews(reviewsPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trial Invites Tab */}
          <TabsContent value="trials">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Inviti Prova Gratuita (1 Mese)</CardTitle>
                <Button
                  className="bg-pink-500 hover:bg-pink-600"
                  onClick={() => {
                    setShowCreateTrialModal(true);
                    setCreatedInviteUrl(null);
                    setNewTrialData({ email: '', note: '', duration_days: 30, invite_type: 'any' });
                  }}
                  data-testid="create-trial-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Crea Invito
                </Button>
              </CardHeader>
              <CardContent>
                {trialInvites.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nessun invito creato</p>
                ) : (
                  <div className="space-y-4">
                    {trialInvites.map((invite) => (
                      <div 
                        key={invite.id} 
                        className={`border rounded-md p-4 ${invite.used ? 'bg-green-50 border-green-200' : 'bg-white'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {invite.used ? (
                                <Badge className="bg-green-100 text-green-700">Usato</Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-700">Attivo</Badge>
                              )}
                              {invite.invite_type === 'professional' && (
                                <Badge className="bg-purple-100 text-purple-700">Professionista</Badge>
                              )}
                              {invite.invite_type === 'end_user' && (
                                <Badge className="bg-teal-100 text-teal-700">Utente Finale</Badge>
                              )}
                              {(!invite.invite_type || invite.invite_type === 'any') && (
                                <Badge className="bg-slate-100 text-slate-600">Libero</Badge>
                              )}
                              {invite.email && (
                                <span className="text-sm text-slate-600">{invite.email}</span>
                              )}
                            </div>
                            
                            {invite.note && (
                              <p className="text-sm text-slate-600 mb-2">{invite.note}</p>
                            )}
                            
                            {!invite.used && (
                              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded text-sm">
                                <code className="flex-1 text-xs break-all">{invite.invite_url}</code>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => copyToClipboard(invite.invite_url)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                            
                            {invite.used && invite.used_by_user && (
                              <p className="text-sm text-green-700">
                                Usato da: {invite.used_by_user.name} ({invite.used_by_user.email})
                              </p>
                            )}
                            
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Creato: {new Date(invite.created_at).toLocaleDateString()}
                              </span>
                              <span>
                                Scade: {new Date(invite.expires_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          
                          <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteTrialInvite(invite.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={trialInvitesPage === 1}
                    onClick={() => fetchTrialInvites(trialInvitesPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-2 text-sm">
                    Page {trialInvitesPage} of {Math.ceil(trialInvitesTotal / 10) || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={trialInvitesPage >= Math.ceil(trialInvitesTotal / 10)}
                    onClick={() => fetchTrialInvites(trialInvitesPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Expiring Trial Users */}
            <Card className="mt-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-orange-500" />
                  Utenti Trial in Scadenza
                </CardTitle>
                <Button
                  className="bg-orange-500 hover:bg-orange-600"
                  onClick={handleSendExpiryNotifications}
                  disabled={sendingNotifications}
                  data-testid="send-notifications-btn"
                >
                  {sendingNotifications ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Invia Notifiche (7 giorni)
                </Button>
              </CardHeader>
              <CardContent>
                {expiringUsers.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nessun utente trial in scadenza nelle prossime 2 settimane</p>
                ) : (
                  <div className="space-y-3">
                    {expiringUsers.map((user) => (
                      <div 
                        key={user.id} 
                        className={`border rounded-md p-4 ${
                          user.days_left <= 3 ? 'bg-red-50 border-red-200' : 
                          user.days_left <= 7 ? 'bg-orange-50 border-orange-200' : 
                          'bg-yellow-50 border-yellow-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{user.name}</p>
                            <p className="text-sm text-slate-600">{user.email}</p>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              user.days_left <= 3 ? 'bg-red-100 text-red-700' : 
                              user.days_left <= 7 ? 'bg-orange-100 text-orange-700' : 
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {user.days_left <= 0 ? 'Scaduto!' : `${user.days_left} giorni`}
                            </Badge>
                            <p className="text-xs text-slate-500 mt-1">
                              Scade: {new Date(user.trial_expires_at).toLocaleDateString()}
                            </p>
                            {user.trial_expiry_notified_7days && (
                              <p className="text-xs text-green-600 mt-1">✓ Notificato</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Professionals Tab */}
          <TabsContent value="professionals">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Gestione Professionisti
                </CardTitle>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowCreateProfessionalModal(true)}
                  data-testid="create-professional-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Aggiungi Professionista
                </Button>
              </CardHeader>
              <CardContent>
                {professionals.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nessun professionista registrato</p>
                ) : (
                  <div className="space-y-4">
                    {professionals.map((prof) => (
                      <div key={prof.id} className="border rounded-md p-4 bg-white">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center">
                                {prof.logo_url ? (
                                  <img src={prof.logo_url} alt={prof.company_name} className="w-full h-full object-cover rounded-md" />
                                ) : (
                                  <Building className="h-6 w-6 text-slate-400" />
                                )}
                              </div>
                              <div>
                                <h3 className="font-semibold">{prof.company_name || prof.name}</h3>
                                <p className="text-sm text-slate-600">{prof.email}</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 mt-3">
                              {prof.city && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {prof.city}, {prof.country}
                                </div>
                              )}
                              {prof.phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {prof.phone}
                                </div>
                              )}
                              {prof.website && (
                                <div className="flex items-center gap-1">
                                  <Globe className="h-3 w-3" />
                                  <a href={prof.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                                    {prof.website}
                                  </a>
                                </div>
                              )}
                            </div>
                            
                            {/* Specializations */}
                            {prof.specializations && Object.keys(prof.specializations).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-3">
                                {Object.entries(prof.specializations).map(([cat, specs]) => (
                                  specs.map(spec => (
                                    <Badge key={`${cat}-${spec}`} variant="secondary" className="text-xs">
                                      {spec.replace(/_/g, ' ')}
                                    </Badge>
                                  ))
                                ))}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                              <span>Creato: {new Date(prof.created_at).toLocaleDateString()}</span>
                              {prof.created_by_admin && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                              )}
                            </div>
                          </div>
                          
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteProfessional(prof.id, prof.email)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={professionalsPage === 1}
                    onClick={() => fetchProfessionals(professionalsPage - 1)}
                  >
                    Previous
                  </Button>
                  <span className="px-3 py-2 text-sm">
                    Page {professionalsPage} of {Math.ceil(professionalsTotal / 10) || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={professionalsPage >= Math.ceil(professionalsTotal / 10)}
                    onClick={() => fetchProfessionals(professionalsPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banners Tab */}
          <TabsContent value="banners">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-orange-500" />
                  Banner Pubblicitari (Corsi di Specializzazione)
                </CardTitle>
                <Button
                  className="bg-orange-500 hover:bg-orange-600"
                  onClick={openCreateBanner}
                  data-testid="create-banner-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Banner
                </Button>
              </CardHeader>
              <CardContent>
                {banners.length === 0 ? (
                  <div className="text-center py-12">
                    <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Nessun banner pubblicitario creato</p>
                    <p className="text-xs text-slate-400 mt-1">
                      I banner appariranno nella Directory ogni 5 schede professionisti
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {banners.map((b) => {
                      const isActive = b.active && new Date(b.start_date) <= new Date() && new Date(b.end_date) >= new Date();
                      const ctr = b.impressions > 0 ? ((b.clicks / b.impressions) * 100).toFixed(1) : '0.0';
                      return (
                        <div
                          key={b.id}
                          className="border rounded-md p-4 bg-white flex flex-col sm:flex-row gap-4"
                          data-testid={`banner-row-${b.id}`}
                        >
                          <div className="w-full sm:w-32 h-24 flex-shrink-0 bg-slate-100 rounded-md overflow-hidden">
                            {b.image_url ? (
                              <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="h-6 w-6 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <Badge className={isActive ? 'bg-green-100 text-green-700 border-green-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                                    {isActive ? 'Attivo' : 'Inattivo'}
                                  </Badge>
                                  <span className="text-xs text-slate-500 uppercase font-mono tracking-wider">{b.company}</span>
                                  {b.target_language && (
                                    <Badge variant="outline" className="text-xs">
                                      {b.target_language.toUpperCase()}
                                    </Badge>
                                  )}
                                </div>
                                <h4 className="font-semibold text-slate-900 truncate">{b.title}</h4>
                                <p className="text-sm text-slate-600 line-clamp-2">{b.description}</p>
                                <p className="text-xs text-slate-500 mt-2">
                                  {(b.start_date || '').split('T')[0]} → {(b.end_date || '').split('T')[0]}
                                </p>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                <div className="text-right">
                                  <div className="flex gap-3 text-xs">
                                    <div>
                                      <p className="font-semibold text-slate-900">{b.impressions}</p>
                                      <p className="text-slate-500">Views</p>
                                    </div>
                                    <div>
                                      <p className="font-semibold text-slate-900">{b.clicks}</p>
                                      <p className="text-slate-500">Clicks</p>
                                    </div>
                                    <div>
                                      <p className="font-semibold text-orange-600">{ctr}%</p>
                                      <p className="text-slate-500">CTR</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 flex-wrap">
                              <Button size="sm" variant="outline" onClick={() => toggleBannerActive(b)} data-testid={`toggle-banner-${b.id}`}>
                                {b.active ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                                {b.active ? 'Disattiva' : 'Attiva'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openEditBanner(b)} data-testid={`edit-banner-${b.id}`}>
                                <Pencil className="h-3.5 w-3.5 mr-1" />
                                Modifica
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteBanner(b.id, b.title)} data-testid={`delete-banner-${b.id}`}>
                                <Trash2 className="h-3.5 w-3.5 mr-1" />
                                Elimina
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Banner Create/Edit Modal */}
        {showBannerModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="banner-modal">
            <Card className="w-full max-w-2xl my-8">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-orange-500" />
                  {editingBanner ? 'Modifica Banner' : 'Nuovo Banner'}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowBannerModal(false); resetBannerForm(); }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="max-h-[70vh] overflow-y-auto space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome azienda *</Label>
                    <Input
                      value={bannerForm.company}
                      onChange={(e) => setBannerForm({ ...bannerForm, company: e.target.value })}
                      placeholder="Es: Siemens Academy"
                      data-testid="banner-input-company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Titolo corso *</Label>
                    <Input
                      value={bannerForm.title}
                      onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                      placeholder="Es: Corso PLC TIA Portal Avanzato"
                      data-testid="banner-input-title"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione *</Label>
                  <textarea
                    className="w-full rounded-md border border-slate-200 p-2 text-sm min-h-[80px]"
                    value={bannerForm.description}
                    onChange={(e) => setBannerForm({ ...bannerForm, description: e.target.value })}
                    placeholder="Breve descrizione del corso (2-3 righe)"
                    maxLength={300}
                    data-testid="banner-input-description"
                  />
                  <p className="text-xs text-slate-500">{bannerForm.description.length}/300</p>
                </div>
                <div className="space-y-2">
                  <Label>Link (URL del corso) *</Label>
                  <Input
                    type="url"
                    value={bannerForm.link_url}
                    onChange={(e) => setBannerForm({ ...bannerForm, link_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="banner-input-link"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Immagine banner (max 2MB)</Label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleBannerImageUpload}
                    className="block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    data-testid="banner-input-image"
                  />
                  {bannerForm.image_url && (
                    <div className="w-full h-40 bg-slate-100 rounded-md overflow-hidden">
                      <img src={bannerForm.image_url} alt="preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Data inizio</Label>
                    <Input
                      type="date"
                      value={bannerForm.start_date}
                      onChange={(e) => setBannerForm({ ...bannerForm, start_date: e.target.value })}
                      data-testid="banner-input-start"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data fine</Label>
                    <Input
                      type="date"
                      value={bannerForm.end_date}
                      onChange={(e) => setBannerForm({ ...bannerForm, end_date: e.target.value })}
                      data-testid="banner-input-end"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lingua target</Label>
                    <select
                      className="w-full rounded-md border border-slate-200 p-2 text-sm h-10"
                      value={bannerForm.target_language}
                      onChange={(e) => setBannerForm({ ...bannerForm, target_language: e.target.value })}
                      data-testid="banner-input-lang"
                    >
                      <option value="all">Tutte le lingue</option>
                      <option value="it">Solo Italiano</option>
                      <option value="en">Solo Inglese</option>
                      <option value="pl">Solo Polacco</option>
                      <option value="de">Solo Tedesco</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="banner-active"
                    checked={bannerForm.active}
                    onChange={(e) => setBannerForm({ ...bannerForm, active: e.target.checked })}
                    data-testid="banner-input-active"
                  />
                  <Label htmlFor="banner-active">Banner attivo</Label>
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => { setShowBannerModal(false); resetBannerForm(); }}>
                    Annulla
                  </Button>
                  <Button
                    className="bg-orange-500 hover:bg-orange-600"
                    onClick={saveBanner}
                    disabled={savingBanner}
                    data-testid="banner-save-btn"
                  >
                    {savingBanner ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {editingBanner ? 'Aggiorna' : 'Crea Banner'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create Professional Modal */}
        {showCreateProfessionalModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <Card className="w-full max-w-2xl my-8">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                  Aggiungi Professionista
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreateProfessionalModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="max-h-[70vh] overflow-y-auto">
                <form onSubmit={handleCreateProfessional} className="space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-700">Informazioni Account</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="profEmail">Email *</Label>
                        <Input
                          id="profEmail"
                          type="email"
                          required
                          value={newProfessionalData.email}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, email: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profPassword">Password *</Label>
                        <Input
                          id="profPassword"
                          type="password"
                          required
                          minLength={6}
                          value={newProfessionalData.password}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, password: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="profName">Nome Referente *</Label>
                        <Input
                          id="profName"
                          required
                          value={newProfessionalData.name}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profCompany">Nome Azienda *</Label>
                        <Input
                          id="profCompany"
                          required
                          value={newProfessionalData.company_name}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, company_name: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-700">Contatti</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="profPhone">Telefono</Label>
                        <Input
                          id="profPhone"
                          value={newProfessionalData.phone}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, phone: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profWebsite">Sito Web</Label>
                        <Input
                          id="profWebsite"
                          type="url"
                          placeholder="https://"
                          value={newProfessionalData.website}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, website: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="profAddress">Indirizzo</Label>
                        <Input
                          id="profAddress"
                          value={newProfessionalData.address}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, address: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profCity">Città</Label>
                        <Input
                          id="profCity"
                          value={newProfessionalData.city}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, city: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profCountry">Paese</Label>
                        <Input
                          id="profCountry"
                          value={newProfessionalData.country}
                          onChange={(e) => setNewProfessionalData({...newProfessionalData, country: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Entity Type */}
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        className={`flex-1 p-3 border rounded-md transition-colors ${
                          newProfessionalData.entity_type === 'freelancer' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200'
                        }`}
                        onClick={() => setNewProfessionalData({...newProfessionalData, entity_type: 'freelancer'})}
                      >
                        Libero Professionista
                      </button>
                      <button
                        type="button"
                        className={`flex-1 p-3 border rounded-md transition-colors ${
                          newProfessionalData.entity_type === 'company' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200'
                        }`}
                        onClick={() => setNewProfessionalData({...newProfessionalData, entity_type: 'company'})}
                      >
                        Azienda
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="profDescription">Descrizione</Label>
                    <textarea
                      id="profDescription"
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md"
                      placeholder="Descrivi i servizi offerti..."
                      value={newProfessionalData.description}
                      onChange={(e) => setNewProfessionalData({...newProfessionalData, description: e.target.value})}
                    />
                  </div>

                  {/* Specializations */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-700">Specializzazioni</h3>
                    {Object.entries(SPECIALIZATIONS).map(([category, specs]) => (
                      <div key={category} className="border rounded-md p-3">
                        <p className="font-medium capitalize mb-2">{category}</p>
                        <div className="flex flex-wrap gap-2">
                          {specs.map(spec => (
                            <button
                              key={spec}
                              type="button"
                              className={`px-2 py-1 text-xs rounded border transition-colors ${
                                (newProfessionalData.specializations[category] || []).includes(spec)
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-white text-slate-700 border-slate-300 hover:border-blue-300'
                              }`}
                              onClick={() => toggleSpecialization(category, spec)}
                            >
                              {spec.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Regions */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-700">Aree Operative</h3>
                    <div className="flex flex-wrap gap-2">
                      {REGIONS.map(region => (
                        <button
                          key={region}
                          type="button"
                          className={`px-3 py-1 text-sm rounded border transition-colors ${
                            newProfessionalData.regions.includes(region)
                              ? 'bg-green-500 text-white border-green-500'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-green-300'
                          }`}
                          onClick={() => toggleRegion(region)}
                        >
                          {region.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Certifications */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-700">Certificazioni</h3>
                    {Object.entries(CERTIFICATIONS).map(([category, certs]) => (
                      <div key={category} className="border rounded-md p-3">
                        <p className="font-medium capitalize mb-2">{category.replace(/_/g, ' ')}</p>
                        <div className="flex flex-wrap gap-2">
                          {certs.map(cert => (
                            <button
                              key={cert}
                              type="button"
                              className={`px-2 py-1 text-xs rounded border transition-colors ${
                                (newProfessionalData.certifications[category] || []).includes(cert)
                                  ? 'bg-orange-500 text-white border-orange-500'
                                  : 'bg-white text-slate-700 border-slate-300 hover:border-orange-300'
                              }`}
                              onClick={() => toggleCertification(category, cert)}
                            >
                              {cert.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Terms Acceptance */}
                  <div className="flex items-start gap-3 pt-4 border-t border-slate-200">
                    <input
                      type="checkbox"
                      id="acceptTermsPro"
                      checked={acceptedTermsPro}
                      onChange={(e) => setAcceptedTermsPro(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      data-testid="accept-terms-pro-checkbox"
                    />
                    <label htmlFor="acceptTermsPro" className="text-sm text-slate-600">
                      Confermo che il professionista ha accettato i{' '}
                      <a href="/terms" target="_blank" className="text-blue-600 hover:underline">
                        Termini di Servizio
                      </a>
                      {' '}e la{' '}
                      <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">
                        Privacy Policy
                      </a>
                    </label>
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowCreateProfessionalModal(false)}
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={creatingProfessional || !acceptedTermsPro}
                    >
                      {creatingProfessional ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Crea Professionista
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create Trial Invite Modal */}
        {showCreateTrialModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-pink-500" />
                  Crea Invito Prova
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreateTrialModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {createdInviteUrl ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <p className="text-green-700 font-medium mb-2">✓ Invito creato con successo!</p>
                      <p className="text-sm text-slate-600 mb-3">Copia e invia questo link all'utente:</p>
                      <div className="flex items-center gap-2 bg-white p-2 rounded border">
                        <code className="flex-1 text-xs break-all">{createdInviteUrl}</code>
                        <Button
                          size="sm"
                          onClick={() => copyToClipboard(createdInviteUrl)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => setShowCreateTrialModal(false)}
                    >
                      Chiudi
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleCreateTrialInvite} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Tipo di invito</Label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          className={`p-3 border rounded-md text-sm transition-colors ${
                            newTrialData.invite_type === 'any'
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setNewTrialData({...newTrialData, invite_type: 'any'})}
                          data-testid="invite-type-any"
                        >
                          Libero (scelta utente)
                        </button>
                        <button
                          type="button"
                          className={`p-3 border rounded-md text-sm transition-colors ${
                            newTrialData.invite_type === 'professional'
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setNewTrialData({...newTrialData, invite_type: 'professional'})}
                          data-testid="invite-type-professional"
                        >
                          Professionista / Azienda
                        </button>
                        <button
                          type="button"
                          className={`p-3 border rounded-md text-sm transition-colors ${
                            newTrialData.invite_type === 'end_user'
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => setNewTrialData({...newTrialData, invite_type: 'end_user'})}
                          data-testid="invite-type-enduser"
                        >
                          Utente Finale
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="trialEmail">Email (opzionale)</Label>
                      <Input
                        id="trialEmail"
                        type="email"
                        placeholder="utente@esempio.com"
                        value={newTrialData.email}
                        onChange={(e) => setNewTrialData({...newTrialData, email: e.target.value})}
                      />
                      <p className="text-xs text-slate-500">Per riferimento - non limita chi può usare il link</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="trialNote">Note (opzionale)</Label>
                      <Input
                        id="trialNote"
                        placeholder="Es: Demo per cliente ABC"
                        value={newTrialData.note}
                        onChange={(e) => setNewTrialData({...newTrialData, note: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="trialDuration">Durata (giorni)</Label>
                      <Input
                        id="trialDuration"
                        type="number"
                        min="1"
                        max="365"
                        value={newTrialData.duration_days}
                        onChange={(e) => setNewTrialData({...newTrialData, duration_days: parseInt(e.target.value) || 30})}
                      />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowCreateTrialModal(false)}
                      >
                        Annulla
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-pink-500 hover:bg-pink-600"
                        disabled={creatingTrial}
                      >
                        {creatingTrial ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        Crea Invito
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

    {/* Convert to Professional Dialog */}
    {convertDialog && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Converti in Professionista</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setConvertDialog(null)}>
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500">
              Utente: <strong>{convertDialog.name}</strong> ({convertDialog.email})
            </p>

            <div className="space-y-2">
              <Label>Nome Azienda / Attività *</Label>
              <Input 
                value={convertForm.company_name}
                onChange={(e) => setConvertForm({...convertForm, company_name: e.target.value})}
                data-testid="convert-company"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <div className="flex gap-3">
                <Button size="sm" variant={convertForm.entity_type === 'freelancer' ? 'default' : 'outline'}
                  onClick={() => setConvertForm({...convertForm, entity_type: 'freelancer'})}>
                  Libero Professionista
                </Button>
                <Button size="sm" variant={convertForm.entity_type === 'company' ? 'default' : 'outline'}
                  onClick={() => setConvertForm({...convertForm, entity_type: 'company'})}>
                  Azienda
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input value={convertForm.phone}
                  onChange={(e) => setConvertForm({...convertForm, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Città</Label>
                <Input value={convertForm.city}
                  onChange={(e) => setConvertForm({...convertForm, city: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Indirizzo</Label>
              <Input value={convertForm.address}
                onChange={(e) => setConvertForm({...convertForm, address: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Regioni</Label>
              <div className="flex flex-wrap gap-2">
                {['europe', 'asia', 'america', 'south_america', 'africa'].map(region => (
                  <Button key={region} size="sm"
                    variant={convertForm.regions.includes(region) ? 'default' : 'outline'}
                    onClick={() => {
                      const regions = convertForm.regions.includes(region)
                        ? convertForm.regions.filter(r => r !== region)
                        : [...convertForm.regions, region];
                      setConvertForm({...convertForm, regions});
                    }}>
                    {region === 'europe' ? 'Europa' : region === 'asia' ? 'Asia' : region === 'america' ? 'America' : region === 'south_america' ? 'Sud America' : 'Africa'}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrizione</Label>
              <textarea className="w-full border rounded-md p-2 text-sm min-h-[60px]"
                value={convertForm.description}
                onChange={(e) => setConvertForm({...convertForm, description: e.target.value})}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setConvertDialog(null)} className="flex-1">
                Annulla
              </Button>
              <Button onClick={handleConvertToProfessional} className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="convert-confirm">
                <UserPlus className="h-4 w-4 mr-2" />
                Converti
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )}
    </div>
  );
};

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return true;
    }
    return false;
  });

  if (!isLoggedIn) {
    return <AdminLoginPage onLogin={() => setIsLoggedIn(true)} />;
  }

  return <AdminDashboard onLogout={() => setIsLoggedIn(false)} />;
};

export default AdminPage;
